export const WEBCASTS_USER_MANUAL = {
  id: "webcasts-user-manual",
  title: "Webcasts User Manual",
  description:
    "Complete guide to managing webcasts — creating events, managing panelists, polls, sending invitations, tracking registrations, attendance, recordings, and more.",
  icon: "Video",
  version: "1.0.0",
  lastUpdated: "2026-02-19",
  requires: ["zoom"],
  sections: [
    /* ---------------------------------------------------------- */
    /*  Overview                                                  */
    /* ---------------------------------------------------------- */
    {
      id: "overview",
      title: "Overview",
      content: [
        {
          type: "paragraph",
          text: "The **Webcasts** module lets you create and manage Zoom webinars directly from Insights. You can invite contacts, track registrations, monitor attendance, access recordings, and generate branded intro videos — all without leaving the platform.",
        },
        {
          type: "callout",
          variant: "info",
          text: "Webcasts are powered by the Zoom Webinar API. Each webcast you create in Insights automatically creates a corresponding Zoom webinar with practice session enabled and cloud recording turned on.",
        },
        {
          type: "paragraph",
          text: "Key capabilities:",
        },
        {
          type: "steps",
          items: [
            "**Create webcasts** with a title, description, date/time, and duration",
            "**Send branded email invitations** to selected contacts",
            "**Manage panelists** — add speakers, send panelist-specific invitations with join links, and automated prep reminders",
            "**Live polls** — create poll questions that can be edited even while the webinar is running",
            "**Automated reminders** for unregistered invitees, registered attendees, and panelists",
            "**Public registration page** — contacts click a link to register themselves",
            "**Attendance reconciliation** — see who attended, who was a no-show",
            "**Cloud recordings** — access and play back recordings after the event",
            "**Branded intro videos** — generate a looping title card to play before going live",
          ],
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Navigating the Webcasts Page                              */
    /* ---------------------------------------------------------- */
    {
      id: "navigating",
      title: "Navigating the Webcasts Page",
      content: [
        {
          type: "paragraph",
          text: "Open the **Webcasts** page from the sidebar navigation. The main page shows a table of all your webcasts with the following columns:",
        },
        {
          type: "table",
          headers: ["Column", "Description"],
          rows: [
            ["**Webcast**", "Title and description of the event"],
            ["**Date**", "Scheduled date and start time"],
            ["**Duration**", "Length of the webinar in minutes"],
            ["**Invited**", "Number of invitation emails sent"],
            ["**Registered**", "Number of contacts who registered"],
            ["**Status**", "Scheduled, Completed, or Cancelled"],
          ],
        },
        {
          type: "heading",
          text: "Filtering Webcasts",
        },
        {
          type: "paragraph",
          text: "Two filter tabs appear above the table:",
        },
        {
          type: "steps",
          items: [
            "**Upcoming** — shows only webcasts that haven't happened yet (default view)",
            "**All Webcasts** — shows every webcast including completed and cancelled events",
          ],
        },
        {
          type: "paragraph",
          text: "When viewing **All Webcasts**, any past webcast campaigns sent through GoHighLevel (GHL) will also appear in a separate section at the bottom of the page.",
        },
        {
          type: "heading",
          text: "Page Actions",
        },
        {
          type: "paragraph",
          text: "The top-right corner of the page has two buttons:",
        },
        {
          type: "steps",
          items: [
            "**Upload Intro Video** (or **Replace Intro Video** if one exists) — uploads a template video used to generate branded intro cards",
            "**Create Webcast** — opens the form to schedule a new webcast",
          ],
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Creating a Webcast                                        */
    /* ---------------------------------------------------------- */
    {
      id: "creating-a-webcast",
      title: "Creating a Webcast",
      content: [
        {
          type: "paragraph",
          text: "Click the **Create Webcast** button in the top-right corner. A form appears with the following fields:",
        },
        {
          type: "table",
          headers: ["Field", "Required", "Description"],
          rows: [
            [
              "**Title**",
              "Yes",
              'The name of the webcast (e.g. "Q1 Advisory Webcast")',
            ],
            [
              "**Description**",
              "No",
              "A short description shown in the event listing",
            ],
            [
              "**Date & Time**",
              "Yes",
              "When the webinar is scheduled to start",
            ],
            ["**Duration**", "No", "Length in minutes (default: 60)"],
            [
              "**Register reminders**",
              "No",
              "Comma-separated days before the event to remind unregistered invitees (default: 7, 3)",
            ],
            [
              "**Webcast reminders**",
              "No",
              "Comma-separated days before the event to remind registered attendees (default: 1)",
            ],
            [
              "**Panelist reminders**",
              "No",
              "Comma-separated days before the event to send prep reminders to panelists (default: 7, 1)",
            ],
          ],
        },
        {
          type: "steps",
          items: [
            "Fill in the **Title** and **Date & Time** at minimum",
            "Adjust the reminder settings if you want different reminder timing",
            "Click **Create Webcast**",
          ],
        },
        {
          type: "callout",
          variant: "tip",
          text: "Insights automatically creates the Zoom webinar with a practice session enabled and cloud recording turned on. You don't need to configure anything in Zoom separately.",
        },
        {
          type: "paragraph",
          text: "Once created, the webcast appears in the table. Click on it to open the detail panel.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  The Event Detail Panel                                    */
    /* ---------------------------------------------------------- */
    {
      id: "event-detail-panel",
      title: "The Event Detail Panel",
      content: [
        {
          type: "paragraph",
          text: "Click any webcast row to open a detail panel on the right side of the screen. The panel shows:",
        },
        {
          type: "steps",
          items: [
            "**Title and status** — the webcast name with a Scheduled / Completed / Cancelled badge",
            "**Date and time** — the scheduled date, start time, and duration",
            "**Description** — the event description (if provided)",
            "**Stats row** — number of panelists, invitations sent, and registrations received",
            "**Intro video status** — whether a branded intro video has been generated",
          ],
        },
        {
          type: "heading",
          text: "Action Buttons",
        },
        {
          type: "paragraph",
          text: "For scheduled webcasts, two action buttons appear:",
        },
        {
          type: "steps",
          items: [
            "**Send Invitations** — opens the invitation modal to select contacts and send emails",
            "**Cancel Webcast** — cancels the webcast and the associated Zoom webinar (requires confirmation)",
          ],
        },
        {
          type: "heading",
          text: "Detail Tabs",
        },
        {
          type: "paragraph",
          text: "Below the header, four tabs provide detailed information:",
        },
        {
          type: "table",
          headers: ["Tab", "Description"],
          rows: [
            [
              "**Panelists**",
              "Search and add panelists, send panelist-specific invitations with join-as-panelist links, copy join URLs, remove panelists",
            ],
            [
              "**Polls**",
              "Create, edit, and delete poll questions — editable even while the webinar is live",
            ],
            [
              "**Invitations**",
              "List of all contacts who have been sent an invitation, with status (Invited or Registered)",
            ],
            [
              "**Registrations**",
              "List of contacts who registered, with their source and current status",
            ],
            [
              "**Attendance**",
              "Summary cards showing registered, attended, no-show, and cancelled counts",
            ],
            [
              "**Recordings**",
              "List of cloud recordings with file type, size, and a link to play",
            ],
          ],
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Sending Invitations                                       */
    /* ---------------------------------------------------------- */
    {
      id: "sending-invitations",
      title: "Sending Invitations",
      content: [
        {
          type: "paragraph",
          text: "Click **Send Invitations** on a scheduled webcast to open the invitation workflow. This is a two-step process: **Design** your email, then **Send** it.",
        },
        {
          type: "heading",
          text: "Step 1 — Design the Email",
        },
        {
          type: "paragraph",
          text: "The first screen shows a rich text editor pre-loaded with a branded email template. You can:",
        },
        {
          type: "steps",
          items: [
            "Edit the **subject line** at the top",
            "Customise the **email body** using the rich text editor (bold, paragraphs, links, images)",
            "Insert **merge tags** — dynamic placeholders that are replaced per-contact when sent",
            "Preview the email on **desktop** or **mobile** widths",
          ],
        },
        {
          type: "paragraph",
          text: "Available merge tags:",
        },
        {
          type: "table",
          headers: ["Merge Tag", "Description", "Example"],
          rows: [
            ["`{{first_name}}`", "Contact's first name", "Sarah"],
            ["`{{last_name}}`", "Contact's last name", "Chen"],
            ["`{{company}}`", "Contact's company name", "Meridian Capital"],
            [
              "`{{registration_link}}`",
              "Unique registration link for the contact",
              "https://...",
            ],
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "The `{{registration_link}}` merge tag is automatically included in the default template. Each contact receives a unique link that pre-fills their details on the registration page.",
        },
        {
          type: "heading",
          text: "Promoting Webcasts in Newsletters",
        },
        {
          type: "paragraph",
          text: "You can also promote webcasts in your regular newsletters. In the **Newsletter Editor**, click **Merge Tags > Webcast Registration Link** and select the webcast. A registration button is inserted into the newsletter. When sent, each recipient gets a unique, secure registration link — the same HMAC-signed tokens used by direct invitations.",
        },
        {
          type: "callout",
          variant: "tip",
          text: "This is useful for reaching contacts who aren't on your direct invitation list, or for including a webcast CTA alongside other newsletter content.",
        },
        {
          type: "heading",
          text: "Step 2 — Select Contacts & Send",
        },
        {
          type: "paragraph",
          text: "Click the arrow to advance to the send step. Here you can:",
        },
        {
          type: "steps",
          items: [
            "**Search** for contacts by name or email",
            "**Filter by type** — All, Clients, Prospects, Subscribers, or Media",
            "**Select individual contacts** by clicking checkboxes, or use **Select All** for the filtered list",
            "Choose your **send method** (see table below)",
            "If using Office 365 or Hybrid, select which email address to send from (your delegate accounts are shown)",
            "Click **Send Invitations** to dispatch the emails",
          ],
        },
        {
          type: "heading",
          text: "Send Methods",
        },
        {
          type: "table",
          headers: ["Method", "Description"],
          rows: [
            [
              "**SendGrid**",
              "Sends via the bulk email service from the shared info@ address. Best for large batches.",
            ],
            [
              "**Office 365**",
              "Sends from your personal Outlook account (or a delegate). Emails appear in your Sent folder and tend to get higher engagement.",
            ],
            [
              "**Draft Only**",
              "Creates draft emails in your Outlook Drafts folder instead of sending. Useful for review before sending manually.",
            ],
            [
              "**Hybrid**",
              "Contacts tagged **key-contact** are sent via your personal O365 account; all other contacts go via SendGrid. Gives VIP contacts the personal touch while keeping the bulk send efficient.",
            ],
          ],
        },
        {
          type: "callout",
          variant: "warning",
          text: "Contacts who have already been invited to this webcast will appear in the list but won't receive duplicate invitations.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Quick Invite (Late Attendees)                             */
    /* ---------------------------------------------------------- */
    {
      id: "quick-invite",
      title: "Quick Invite (Late Attendees)",
      content: [
        {
          type: "paragraph",
          text: "Need to invite someone after the main invitation batch has already been sent? Use the **Quick Invite** feature built into the Invitations tab of the event detail panel.",
        },
        {
          type: "heading",
          text: "How to Quick Invite",
        },
        {
          type: "steps",
          items: [
            "Click on a **scheduled** webcast to open the detail panel",
            "Switch to the **Invitations** tab — the Quick Invite search box appears at the top",
            "Choose your send method: **Personal O365** or **SendGrid**",
            "If using O365, select your delegate account from the dropdown (if you have more than one)",
            "Type a **name or email** in the search box — matching contacts appear instantly",
            "Contacts who have already been invited are automatically excluded from results",
            "Click the **Invite** button next to the contact to send the invitation immediately",
            "A ✓ checkmark confirms the send, and the invitation list refreshes automatically",
          ],
        },
        {
          type: "callout",
          variant: "tip",
          text: "Quick Invite sends the invitation without a custom email body — the contact receives the default invitation. For a customised email, use the full Send Invitations workflow instead.",
        },
        {
          type: "callout",
          variant: "info",
          text: "Quick Invite only appears for webcasts in **Scheduled** status. Completed or cancelled webcasts do not show the search box.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Panelist Management                                       */
    /* ---------------------------------------------------------- */
    {
      id: "panelist-management",
      title: "Panelist Management",
      content: [
        {
          type: "paragraph",
          text: "The **Panelists** tab in the event detail panel lets you manage speakers and presenters for the webinar. Panelists join through a special panelist link (not the attendee link) and have elevated permissions in Zoom — they can share their camera, screen, and unmute themselves.",
        },
        {
          type: "heading",
          text: "Adding Panelists",
        },
        {
          type: "steps",
          items: [
            "Open a scheduled webcast and switch to the **Panelists** tab",
            "Type a **name or email** in the search box — matching Insights contacts appear",
            "Click **Add** next to a contact to add them as a panelist",
            "The system registers them as a panelist in Zoom and retrieves their unique **panelist join link**",
          ],
        },
        {
          type: "heading",
          text: "Sending Panelist Invitations",
        },
        {
          type: "steps",
          items: [
            "Choose your send method: **Personal O365** or **SendGrid**",
            'Click the **send** icon (paper plane) next to a panelist with "Added" status',
            "The panelist receives a branded email with their **join-as-panelist link** and a **prep checklist** (join 15 min early, test camera/mic, use panelist link, professional background)",
            "Their status changes from **Added** to **Invited**",
          ],
        },
        {
          type: "heading",
          text: "Other Actions",
        },
        {
          type: "steps",
          items: [
            "**Copy join link** — click the copy icon to copy a panelist's unique join URL to your clipboard (useful for sharing via chat or calendar invite)",
            "**Remove panelist** — click the trash icon to remove a panelist from the Zoom webinar",
          ],
        },
        {
          type: "heading",
          text: "Panelist Status Badges",
        },
        {
          type: "table",
          headers: ["Status", "Meaning"],
          rows: [
            ["**Added**", "Panelist added to Zoom but invitation not yet sent"],
            ["**Invited**", "Panelist invitation email has been sent"],
            ["**Confirmed**", "Panelist has confirmed attendance"],
          ],
        },
        {
          type: "callout",
          variant: "tip",
          text: "Panelists also receive automated prep reminders (default: 7 days and 1 day before the event). You can customise the timing when creating the webcast.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Polls                                                     */
    /* ---------------------------------------------------------- */
    {
      id: "polls",
      title: "Polls",
      content: [
        {
          type: "paragraph",
          text: "The **Polls** tab lets you create interactive poll questions for your webinar. Polls are synced with Zoom and appear to attendees when you launch them during the live session.",
        },
        {
          type: "heading",
          text: "Creating a Poll",
        },
        {
          type: "steps",
          items: [
            "Open a scheduled webcast and switch to the **Polls** tab",
            "Click **Add Poll**",
            'Enter a **poll title** (e.g. "Audience Feedback")',
            "Add one or more **questions** — each needs question text and at least 2 answers",
            "Choose **single choice** or **multiple choice** for each question",
            "Click **Create Poll** to save — the poll is created in Zoom immediately",
          ],
        },
        {
          type: "heading",
          text: "Editing Polls — Even During a Live Webinar",
        },
        {
          type: "paragraph",
          text: "A key feature of polls is that **questions are editable at any time**, including while the webinar is live. This lets you:",
        },
        {
          type: "steps",
          items: [
            "Fix typos or rephrase questions on the fly",
            "Add new answer options based on audience feedback",
            "Prepare follow-up polls during the session",
            "Adapt questions based on how the presentation unfolds",
          ],
        },
        {
          type: "paragraph",
          text: "To edit: click the **pencil icon** on any poll, make your changes, and click **Save Changes**. The update takes effect in Zoom immediately.",
        },
        {
          type: "heading",
          text: "Deleting a Poll",
        },
        {
          type: "paragraph",
          text: "Click the **trash icon** on a poll and confirm. The poll is removed from Zoom and will no longer be available to launch.",
        },
        {
          type: "callout",
          variant: "info",
          text: "To **launch** a poll during a live webinar, use the Polls panel in the Zoom host controls. Insights creates and manages the poll content; Zoom handles the live polling experience (launching, collecting responses, sharing results).",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Registration                                              */
    /* ---------------------------------------------------------- */
    {
      id: "registration",
      title: "Registration",
      content: [
        {
          type: "paragraph",
          text: "When a contact receives an invitation email, they click the registration link to open a branded public registration page. The page:",
        },
        {
          type: "steps",
          items: [
            "Displays the webcast **title, date, and time**",
            "**Pre-fills** the contact's name and email (from the invitation link)",
            "Lets the contact confirm and **submit their registration**",
            "Shows a **success message** with calendar details after registering",
          ],
        },
        {
          type: "paragraph",
          text: "Registration is also available for contacts who weren't originally invited — they can be sent the public URL manually.",
        },
        {
          type: "heading",
          text: "Registration Statuses",
        },
        {
          type: "table",
          headers: ["Status", "Meaning"],
          rows: [
            ["**Registered**", "Contact has completed the registration form"],
            [
              "**Attended**",
              "Contact joined the live webinar (set after attendance reconciliation)",
            ],
            ["**No Show**", "Contact registered but did not attend"],
            ["**Cancelled**", "Registration was cancelled"],
          ],
        },
        {
          type: "paragraph",
          text: "You can monitor registrations in real time on the **Registrations** tab of the event detail panel.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Automated Reminders                                       */
    /* ---------------------------------------------------------- */
    {
      id: "automated-reminders",
      title: "Automated Reminders",
      content: [
        {
          type: "paragraph",
          text: "Insights automatically sends reminder emails based on the schedule you set when creating the webcast. There are three types of reminders:",
        },
        {
          type: "heading",
          text: "Register Reminders",
        },
        {
          type: "paragraph",
          text: "Sent to contacts who were **invited but haven't registered yet**. By default, these go out 7 days and 3 days before the event. The email reminds them to register and includes their unique registration link.",
        },
        {
          type: "heading",
          text: "Event Reminders",
        },
        {
          type: "paragraph",
          text: "Sent to contacts who **have registered**. By default, these go out 1 day before the event. The email confirms the event details and reminds them to join.",
        },
        {
          type: "heading",
          text: "Panelist Reminders",
        },
        {
          type: "paragraph",
          text: "Sent to **panelists** added to the event. By default, these go out 7 days and 1 day before. Panelist reminders include a **prep checklist** (join 15 minutes early, test camera and microphone, use the panelist join link, use a professional background) and an ICS calendar attachment.",
        },
        {
          type: "callout",
          variant: "tip",
          text: 'You can customise reminder timing when creating a webcast. Enter comma-separated numbers for the days before the event you want reminders sent. For example, entering "7, 3, 1" sends reminders at 7 days, 3 days, and 1 day before.',
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Intro Video                                               */
    /* ---------------------------------------------------------- */
    {
      id: "intro-video",
      title: "Intro Video",
      content: [
        {
          type: "paragraph",
          text: "The intro video feature lets you create a branded video with the webcast title overlaid on a template video. This video is **not sent to attendees** — it is downloaded to your computer and played live during the webinar via screen-share.",
        },
        {
          type: "callout",
          variant: "info",
          text: "Think of the intro video as a live holding screen. Before the webinar starts, you screen-share it in Zoom's practice session. When you click Broadcast, attendees see and hear the branded video looping while more people join. When you're ready to present, you stop sharing and begin your presentation on camera.",
        },
        {
          type: "heading",
          text: "Uploading a Template",
        },
        {
          type: "steps",
          items: [
            "Click **Upload Intro Video** in the top-right corner of the Webcasts page",
            "Select a video file (MP4, WebM, or QuickTime, up to 100 MB)",
            "The template is uploaded and stored — you only need to do this once",
          ],
        },
        {
          type: "paragraph",
          text: "The template video should be a looping background animation or branded holding slide. The system will overlay the webcast title text on top of it.",
        },
        {
          type: "heading",
          text: "Generating an Intro Video",
        },
        {
          type: "steps",
          items: [
            "Open the detail panel for a scheduled webcast",
            "Click **Generate Intro Video**",
            "A preview appears showing the template with the title overlaid",
            "Click **Generate** — processing takes around 90 seconds",
            "When complete, click **Download** to save the video to your computer",
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "If you need to update the title or re-render, click **Regenerate** in the event detail panel. This creates a new video with the current title.",
        },
        {
          type: "heading",
          text: "Using the Intro Video on the Day",
        },
        {
          type: "paragraph",
          text: "The intro video is played live via screen-share during the Zoom webinar. Attendees do not receive it ahead of time. Here's the flow:",
        },
        {
          type: "steps",
          items: [
            "Open the downloaded intro video in any video player (VLC, Windows Media Player, etc.) and set it to **loop**",
            "Start the Zoom webinar — you'll be in **practice session** mode where attendees can't see or hear you yet",
            "In Zoom, click **Share Screen**, select the video player window, and tick **Share sound**",
            "When ready, click **Broadcast** — attendees now see and hear the branded intro video looping",
            "Let it play for 1–2 minutes while people join",
            "Click **Stop Share** to end the video and begin your live presentation",
          ],
        },
        {
          type: "callout",
          variant: "tip",
          text: "For detailed step-by-step instructions including Zoom branding setup, see the **Webinar Host Guide** in the Help section.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Attendance                                                */
    /* ---------------------------------------------------------- */
    {
      id: "attendance",
      title: "Attendance",
      content: [
        {
          type: "paragraph",
          text: "After a webinar ends, Insights reconciles attendance data from Zoom. The **Attendance** tab in the event detail panel shows a summary with four cards:",
        },
        {
          type: "table",
          headers: ["Card", "Description"],
          rows: [
            ["**Registered**", "Total number of contacts who registered"],
            ["**Attended**", "Contacts who actually joined the live webinar"],
            ["**No Show**", "Contacts who registered but did not join"],
            ["**Cancelled**", "Contacts whose registration was cancelled"],
          ],
        },
        {
          type: "callout",
          variant: "warning",
          text: 'Zoom\'s attendance report may take 5–10 minutes to become available after the webinar ends. If the Attendance tab shows "Attendance data not yet available", wait a few minutes and reopen the event.',
        },
        {
          type: "paragraph",
          text: "Attendance data is automatically matched against your Insights contacts. Each contact's registration status is updated to reflect whether they attended or were a no-show.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Recordings                                                */
    /* ---------------------------------------------------------- */
    {
      id: "recordings",
      title: "Recordings",
      content: [
        {
          type: "paragraph",
          text: "Cloud recordings are automatically enabled when the webcast is created. After the webinar ends, Zoom processes the recordings and makes them available.",
        },
        {
          type: "paragraph",
          text: "The **Recordings** tab in the event detail panel lists all available recordings with:",
        },
        {
          type: "steps",
          items: [
            '**Type** — e.g. "shared screen with speaker view", "gallery view", "audio only"',
            "**File format and size** — e.g. MP4, 152.3 MB",
            "**Play link** — click to open and play the recording in a new tab",
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "Recordings typically become available within an hour after the webinar ends. Longer webinars may take more time to process.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Cancelling a Webcast                                      */
    /* ---------------------------------------------------------- */
    {
      id: "cancelling",
      title: "Cancelling a Webcast",
      content: [
        {
          type: "paragraph",
          text: "To cancel a scheduled webcast:",
        },
        {
          type: "steps",
          items: [
            "Click on the webcast to open the detail panel",
            "Click **Cancel Webcast**",
            "Confirm the cancellation in the dialog that appears",
          ],
        },
        {
          type: "callout",
          variant: "warning",
          text: "Cancelling a webcast also cancels the associated Zoom webinar. This action cannot be undone. Contacts who have already registered will not receive an automatic cancellation notice — you should notify them separately if needed.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Quick Reference                                           */
    /* ---------------------------------------------------------- */
    {
      id: "quick-reference",
      title: "Quick Reference",
      content: [
        {
          type: "table",
          headers: ["Task", "How"],
          rows: [
            [
              "Create a webcast",
              "Webcasts page → **Create Webcast** → fill form → **Create Webcast**",
            ],
            [
              "Send invitations",
              "Click webcast → **Send Invitations** → design email → select contacts → **Send**",
            ],
            ["Check registrations", "Click webcast → **Registrations** tab"],
            [
              "View attendance",
              "Click webcast → **Attendance** tab (available after event)",
            ],
            [
              "Watch recordings",
              "Click webcast → **Recordings** tab → **Play**",
            ],
            ["Upload intro template", "Webcasts page → **Upload Intro Video**"],
            [
              "Generate intro video",
              "Click webcast → **Generate Intro Video** → **Generate** → **Download**",
            ],
            [
              "Quick invite a late attendee",
              "Click webcast → **Invitations** tab → search contact → **Invite**",
            ],
            [
              "Add a panelist",
              "Click webcast → **Panelists** tab → search contact → **Add**",
            ],
            [
              "Send panelist invitation",
              "Click webcast → **Panelists** tab → click send icon next to panelist",
            ],
            [
              "Create a poll",
              "Click webcast → **Polls** tab → **Add Poll** → add questions → **Create Poll**",
            ],
            [
              "Edit a poll during webinar",
              "Click webcast → **Polls** tab → click pencil icon → edit → **Save Changes**",
            ],
            [
              "Cancel a webcast",
              "Click webcast → **Cancel Webcast** → confirm",
            ],
            [
              "See past GHL campaigns",
              "Webcasts page → **All Webcasts** tab → scroll down",
            ],
          ],
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Troubleshooting                                           */
    /* ---------------------------------------------------------- */
    {
      id: "troubleshooting",
      title: "Troubleshooting",
      content: [
        {
          type: "faq",
          items: [
            {
              question: "The Webcasts page won't load or shows an error",
              answer:
                "Check your internet connection and try refreshing the page. If the error persists, the Zoom API may be temporarily unavailable. Wait a few minutes and try again.",
            },
            {
              question: "I can't see the Create Webcast button",
              answer:
                "The Webcasts feature is only available to users whose account has the Zoom integration enabled. Contact your administrator if you believe you should have access.",
            },
            {
              question: "Invitations aren't being delivered",
              answer:
                "Check that the contact has a valid email address. If using SendGrid, verify that your SendGrid integration is configured. If using Office 365, ensure you have the correct delegate permissions. Check the SendGrid Activity page for bounce or block details.",
            },
            {
              question: "The registration link doesn't work for a contact",
              answer:
                "Registration links include a security token. If the link has expired or the event has been cancelled, the registration page will show an error. Re-send the invitation to generate a fresh link.",
            },
            {
              question: "Attendance data shows 'not yet available'",
              answer:
                "Zoom's attendance reports take 5–10 minutes to become available after the webinar ends. Close the detail panel, wait a few minutes, and reopen the webcast to check again.",
            },
            {
              question: "No recordings appear after the webinar",
              answer:
                "Zoom cloud recordings can take up to an hour to process after the webinar ends. If recordings still don't appear after an hour, check your Zoom account to verify cloud recording was enabled.",
            },
            {
              question: "The intro video generator isn't showing",
              answer:
                "You need to upload a template video first. Click **Upload Intro Video** on the Webcasts page to upload a template, then return to the webcast detail to generate.",
            },
            {
              question: "The generated intro video has no title overlay",
              answer:
                "Make sure you clicked **Generate Intro Video** (not downloaded the template directly). The generator overlays the webcast title on the template and creates a new video file.",
            },
          ],
        },
      ],
    },
  ],
};
